import { useDispatch, useSelector } from 'react-redux';
import { Label } from 'lib/Label';
import BaseSelect from 'lib/BaseSelect';
import Tooltip, { TooltipDirection } from 'lib/Tooltip';
import {
  DealPriority as DealPriorityType,
  DealPriorityLabel,
  DealType,
  DealStatus,
} from 'components/common/types/Deal.types';
import { InputShape, InputSize, InputTheme } from 'components/common/types/Input.types';
import { Store } from 'components/common/types/Store.types';
import { codeNameDealPriorityOptions } from 'consts/dealPriority';
import { Padding } from 'lib/BaseSelect/BaseSelect.types';
import { ShadowStyle } from 'consts/shadow';
import { changeDealData } from 'store/dealManagement/reducer';
import { CodeNameModel } from 'components/common/types';
import { getIsReseller } from 'store/publisher/selectors';
import { getDealType } from 'store/dealManagement/selectors/getDealType';
import { getIsEditingDisabled } from 'store/dealManagement/selectors/getIsEditingDisabled';
import { isReadOnly } from 'utils/isReadOnly';
import usePendoTracking from 'customHooks/usePendoTracking';

const DealPriority = (): React.ReactElement | null => {
  const dispatch = useDispatch();

  const dealPriority = useSelector((state: Store) => state.dealManagement.commonDeal.dealPriority);
  const isEditingDisabled = useSelector(getIsEditingDisabled);
  const bookingStatusCode = useSelector((state: Store) => state.dealManagement.commonDeal.bookingStatusCode);
  const dealType = useSelector(getDealType);
  const isReseller = useSelector(getIsReseller);
  const hasVIOOHSelectFeatures = useSelector((state: Store) => state.reseller.isInternalPublisher);
  const { getPendoDataId } = usePendoTracking();

  const isDisabled =
    (isReadOnly(bookingStatusCode, isEditingDisabled) && bookingStatusCode !== DealStatus.PENDING_RESERVATION) ||
    (isReseller && !hasVIOOHSelectFeatures);

  const handleSelect = (_name: string, value: CodeNameModel): void => {
    dispatch(changeDealData({ dealPriority: value.code as DealPriorityType }));
  };

  if (dealType === DealType.GUARANTEED) return null;

  const getDealPriorityPendoId = (name: string): string => getPendoDataId(`Deal priority ${name}`);

  const priorityOptions = codeNameDealPriorityOptions.map((option) => ({
    ...option,
    dataPendoId: getDealPriorityPendoId(option.name),
  }));

  return (
    <>
      <Label id="priority" theme={InputTheme.FLAT_GRAY} label="Priority" />
      <Tooltip tooltip={DealPriorityLabel[dealPriority]} direction={TooltipDirection.BOTTOM} isHiddenOnFocus>
        <BaseSelect
          name="Priority"
          options={priorityOptions}
          selectedValue={{
            code: dealPriority,
            name: DealPriorityLabel[dealPriority],
          }}
          onSelect={handleSelect}
          isDisabled={isDisabled}
          theme={InputTheme.WHITE_WITH_BORDER}
          size={InputSize.EXTRA_SMALL}
          padding={Padding.NONE}
          shadow={ShadowStyle.NONE}
          shape={InputShape.ROUNDED}
          dataTestId="priority-select"
          dataPendoId={getDealPriorityPendoId(DealPriorityLabel[dealPriority])}
        />
      </Tooltip>
    </>
  );
};

export default DealPriority;
